import React, { useEffect, useState } from 'react';
import { settlementsApi, clientsApi } from '../../lib/api';
import { useSocketStore } from '../../store/socketStore';
import { GroupBox } from '../shared/GroupBox';
import { DenseTable } from '../shared/DenseTable';
import { StatusBadge } from '../shared/StatusBadge';
import { EntryBar } from '../shared/EntryBar';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { RefreshCw, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

export function ModernSettlementPage() {
  const { subscribe } = useSocketStore();
  const [clients, setClients] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedClient, setSelectedClient] = useState('');
  const [form, setForm] = useState({
    client_id: '',
    amount: '',
    settlement_type: 'PAYMENT',
    notes: ''
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [clientsData, settlementsData] = await Promise.all([
        clientsApi.list(),
        settlementsApi.list(selectedClient || undefined)
      ]);
      setClients(clientsData);
      setSettlements(settlementsData);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const unsub = subscribe('SETTLEMENT_POSTED', fetchData);
    return () => unsub();
  }, [subscribe, selectedClient]);

  const handleSubmit = async () => {
    try {
      if (!form.client_id || !form.amount) {
        toast.error('Client and amount are required');
        return;
      }

      await settlementsApi.create({
        client_id: form.client_id,
        amount: parseFloat(form.amount),
        settlement_type: form.settlement_type,
        notes: form.notes || null
      });

      toast.success('Settlement posted');
      setShowForm(false);
      setForm({ client_id: '', amount: '', settlement_type: 'PAYMENT', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const columns = [
    { header: 'Date', render: (row) => format(new Date(row.timestamp), 'dd/MM/yyyy HH:mm') },
    { header: 'Client', render: (row) => clients.find(c => c.client_id === row.client_id)?.name || row.client_id },
    { header: 'Type', render: (row) => <StatusBadge status={row.settlement_type} /> },
    { 
      header: 'Amount', 
      accessor: 'amount', 
      type: 'number',
      render: (row) => (
        <span className={row.settlement_type === 'RECEIPT' ? 'profit' : 'loss'}>
          {row.settlement_type === 'RECEIPT' ? '+' : '-'}{row.amount?.toFixed(2)}
        </span>
      )
    },
    { header: 'Notes', accessor: 'notes' }
  ];

  // Calculate client balances (simplified)
  const clientBalances = clients.map(client => {
    const clientSettlements = settlements.filter(s => s.client_id === client.client_id);
    const balance = clientSettlements.reduce((acc, s) => {
      return acc + (s.settlement_type === 'RECEIPT' ? s.amount : -s.amount);
    }, 0);
    return { ...client, settlement_balance: balance };
  });

  return (
    <div className="space-y-4" data-testid="settlement-page">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold">Settlement</h1>
        <div className="flex items-center gap-2">
          <Button 
            size="sm" 
            onClick={() => setShowForm(true)}
            data-testid="new-settlement-btn"
          >
            <Plus className="h-4 w-4 mr-1" />
            New Settlement
          </Button>
          <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-settlements">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Settlement Form */}
      {showForm && (
        <GroupBox label="Post Settlement">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="md:col-span-2">
              <Label className="text-xs">Client *</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({...form, client_id: v})}>
                <SelectTrigger className="h-8" data-testid="settlement-client">
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(c => (
                    <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Amount *</Label>
              <Input 
                type="number"
                value={form.amount}
                onChange={(e) => setForm({...form, amount: e.target.value})}
                className="h-8"
                data-testid="settlement-amount"
              />
            </div>
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={form.settlement_type} onValueChange={(v) => setForm({...form, settlement_type: v})}>
                <SelectTrigger className="h-8" data-testid="settlement-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PAYMENT">PAYMENT (Pay to client)</SelectItem>
                  <SelectItem value="RECEIPT">RECEIPT (Receive from client)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-4">
              <Label className="text-xs">Notes</Label>
              <Textarea 
                value={form.notes}
                onChange={(e) => setForm({...form, notes: e.target.value})}
                className="h-16 text-sm"
                placeholder="Optional notes..."
                data-testid="settlement-notes"
              />
            </div>
          </div>
          <EntryBar 
            onSave={handleSubmit} 
            onCancel={() => setShowForm(false)}
            saveLabel="Post Settlement"
          />
        </GroupBox>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Client Balances */}
        <GroupBox label="Client Balances">
          <div className="mb-3">
            <Label className="text-xs">Filter by Client</Label>
            <Select value={selectedClient || "all"} onValueChange={(v) => setSelectedClient(v === "all" ? "" : v)}>
              <SelectTrigger className="h-8" data-testid="filter-client">
                <SelectValue placeholder="All clients" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Clients</SelectItem>
                {clients.map(c => (
                  <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DenseTable 
            columns={[
              { header: 'Client', accessor: 'name' },
              { header: 'Nick', accessor: 'nick_name' },
              { 
                header: 'Settlement Balance', 
                accessor: 'settlement_balance',
                type: 'profit',
                render: (row) => (
                  <span className={row.settlement_balance >= 0 ? 'profit' : 'loss'}>
                    {row.settlement_balance?.toFixed(2)}
                  </span>
                )
              }
            ]} 
            data={clientBalances}
            emptyMessage="No clients"
          />
        </GroupBox>

        {/* Settlement History */}
        <GroupBox label="Settlement History">
          <DenseTable 
            columns={columns} 
            data={settlements}
            emptyMessage="No settlements"
          />
        </GroupBox>
      </div>
    </div>
  );
}
